import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { compareLabels } from '@bre-ticketing/domain';
import { SeatMapView, useSeatSelection } from '@bre-ticketing/seatmap';
import type { ManifestGeometry, SeatGeometry } from '@bre-ticketing/seatmap';
import type { InventoryUnitRow, ManifestLayout } from '../../api';
import { endpoints, inventoryEndpoints } from '../../api';
import { Badge, Button, EmptyRow, Field, Panel, Table } from '../../ui';
import type { PanelProps } from '../Event';
import { INPUT, humanize, inZone } from './shared';
import { dateWords } from '../../terms';

/**
 * Inventory for one performance: initialize it, see every unit's state on the
 * seat map, and change it in bulk — holds, kills, restricted pools, disable.
 * Everything here goes through the performance's Durable Object; the map is a
 * picture of what it says, never a place to edit seats.
 */

const STATE_FILL: Record<string, string> = {
  AVAILABLE: '#d9d9d9',
  PAYMENT_PENDING: '#b3893f',
  SOLD: '#7b1e2b',
  COMP: '#a35a66',
  HOUSE_HOLD: '#2f6fb3',
  ARTIST_HOLD: '#3d8fd6',
  PROMOTER_HOLD: '#4d7fb0',
  SPONSOR_HOLD: '#5aa0d8',
  PRODUCTION_HOLD: '#6bb0e0',
  VIP_HOLD: '#8a5fc9',
  CUSTOM_HOLD: '#9a7fd0',
  PRODUCTION_KILL: '#171a21',
  ADA: '#1d7a4c',
  COMPANION: '#5fb082',
  RESTRICTED_VIEW: '#d99a2b',
  REFUNDED: '#e08a8a',
  EXCHANGE_HOLD: '#c9a24b',
  DISABLED: '#ffffff',
};
const CART_FILL = '#ffcc66';
const HOLD_CLASSES = ['HOUSE', 'PROMOTER', 'ARTIST', 'SPONSOR', 'PRODUCTION', 'VIP'];

export function InventoryPanel({ detail, onError, can }: PanelProps) {
  const [performanceId, setPerformanceId] = useState(detail.performances[0]?.id ?? '');
  const performance = detail.performances.find((p) => p.id === performanceId) ?? null;
  return (
    <div className="space-y-4">
      {detail.performances.length === 0 ? (
        <Panel title="Inventory">
          <p className="px-5 py-4 text-sm text-ink-600">Add {dateWords().a} first; inventory belongs to one.</p>
        </Panel>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <label className="text-sm text-ink-900">
              {dateWords().One}{' '}
              <select value={performanceId} onChange={(e) => setPerformanceId(e.target.value)} className={INPUT}>
                {detail.performances.map((p) => (
                  <option key={p.id} value={p.id}>
                    {inZone(p.starts_at, detail.venue.timezone)} · {p.status.replaceAll('_', ' ').toLowerCase()}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {performance ? <PerformanceInventory key={performance.id} detail={detail} performanceId={performance.id} manifestId={performance.manifest_id} onError={onError} can={can} /> : null}
        </>
      )}
    </div>
  );
}

function PerformanceInventory({ detail, performanceId, manifestId, onError, can }: { detail: PanelProps['detail']; performanceId: string; manifestId: string; onError: PanelProps['onError']; can: PanelProps['can'] }) {
  const queryClient = useQueryClient();
  const eventId = detail.event.id;
  const summary = useQuery({ queryKey: ['inventory', eventId, performanceId], queryFn: () => inventoryEndpoints.summary(eventId, performanceId) });
  const units = useQuery({ queryKey: ['inventory-units', eventId, performanceId], queryFn: () => inventoryEndpoints.units(eventId, performanceId), enabled: summary.data?.initialized === true, refetchInterval: 5000 });
  const layout = useQuery<ManifestLayout>({ queryKey: ['manifest-layout', manifestId], queryFn: () => endpoints.manifestLayout(manifestId) });
  const selection = useSeatSelection();
  const [policy, setPolicy] = useState<'POOL' | 'AVAILABLE'>('POOL');
  const [action, setAction] = useState<'hold' | 'kill' | 'unkill' | 'pool' | 'disable' | 'enable'>('hold');
  const [holdClass, setHoldClass] = useState('HOUSE');
  const [holdName, setHoldName] = useState('');
  const [holdRelease, setHoldRelease] = useState('');
  const [pool, setPool] = useState<'ADA' | 'COMPANION' | 'RESTRICTED_VIEW' | 'RELEASE'>('RESTRICTED_VIEW');
  const [scope, setScope] = useState<'selected' | 'section' | 'row' | 'zone' | 'tables'>('selected');
  const [scopeId, setScopeId] = useState('');
  // Whole tables, ticked (D-129). A cabaret room is sold by the table, and
  // holding six of them by dragging over thirty-six seats is not something
  // anybody should be asked to do on a phone.
  const [tableIds, setTableIds] = useState<string[]>([]);
  const [preempt, setPreempt] = useState(false);
  const [reason, setReason] = useState('');
  const [inspect, setInspect] = useState<InventoryUnitRow | null>(null);
  const history = useQuery({ queryKey: ['inventory-history', eventId, performanceId, inspect?.unit_id], queryFn: () => inventoryEndpoints.history(eventId, performanceId, inspect!.unit_id), enabled: Boolean(inspect) });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['inventory', eventId, performanceId] });
    void queryClient.invalidateQueries({ queryKey: ['inventory-units', eventId, performanceId] });
    void queryClient.invalidateQueries({ queryKey: ['event', eventId] });
  };

  const initialize = useMutation({ mutationFn: () => inventoryEndpoints.initialize(eventId, performanceId, policy), onSuccess: refresh, onError });
  const releaseHold = useMutation({ mutationFn: (holdId: string) => inventoryEndpoints.releaseHold(eventId, performanceId, holdId), onSuccess: refresh, onError });
  const convertHold = useMutation({ mutationFn: ({ holdId, code }: { holdId: string; code: string }) => inventoryEndpoints.convertHold(eventId, performanceId, holdId, code), onSuccess: refresh, onError });
  const remap = useMutation({ mutationFn: () => inventoryEndpoints.remapZones(eventId, performanceId), onSuccess: refresh, onError });

  const unitBySeat = useMemo(() => new Map((units.data?.units ?? []).filter((u) => u.seat_id).map((u) => [u.seat_id!, u])), [units.data]);
  const seatFills = useMemo(() => {
    const fills: Record<string, string> = {};
    for (const [seatId, unit] of unitBySeat) fills[seatId] = unit.cart_held ? CART_FILL : (STATE_FILL[unit.state] ?? '#d9d9d9');
    return fills;
  }, [unitBySeat]);

  const selectedUnitIds = useMemo(() => Array.from(selection.selected, (seatId) => unitBySeat.get(seatId)?.unit_id).filter((id): id is string => Boolean(id)), [selection.selected, unitBySeat]);

  const apply = useMutation({
    mutationFn: async () => {
      const target =
        scope === 'selected'
          ? { unit_ids: selectedUnitIds }
          : scope === 'tables'
            ? { table_ids: tableIds }
            : scope === 'section'
              ? { section_id: scopeId }
              : scope === 'row'
                ? { row_id: scopeId }
                : { price_zone_id: scopeId };
      const common = { ...target, preempt_carts: preempt, reason: reason.trim() || null };
      if (action === 'hold') return inventoryEndpoints.placeHold(eventId, performanceId, { ...common, hold_class_code: holdClass, name: holdName.trim() || `${humanize(holdClass)} hold` , ...(holdRelease ? { auto_release_at: new Date(holdRelease).toISOString().replace(/\.\d{3}Z$/, '.000Z') } : {}) });
      if (action === 'pool') return inventoryEndpoints.pools(eventId, performanceId, { ...common, pool });
      return inventoryEndpoints.change(eventId, performanceId, action, common);
    },
    onSuccess: () => {
      selection.clear();
      refresh();
    },
    onError,
  });

  const canChange = can('inventory.hold.place') || can('inventory.kill') || can('inventory.pool.manage');
  const targetReady = scope === 'selected' ? selectedUnitIds.length > 0 : scope === 'tables' ? tableIds.length > 0 : Boolean(scopeId);
  const geometry = layout.data as unknown as ManifestGeometry | undefined;
  const onSeatClick = (seat: SeatGeometry, modifiers: { shiftKey: boolean; metaKey: boolean }) => {
    selection.clickSeat(seat, modifiers);
    setInspect(unitBySeat.get(seat.id) ?? null);
  };

  if (summary.isPending) return <p className="text-sm text-ink-400">Loading…</p>;

  if (!summary.data?.initialized) {
    return (
      <Panel title="Inventory" description="Creates one unit per active seat and one per unit of GA capacity, in the states the seat attributes imply, priced by the zone assignments.">
        <div className="flex flex-wrap items-end gap-4 px-5 py-4">
          <Field label="Restricted-view seats" htmlFor="inv-policy">
            <select id="inv-policy" value={policy} onChange={(e) => setPolicy(e.target.value as 'POOL' | 'AVAILABLE')} className={INPUT}>
              <option value="POOL">held in the restricted-view pool</option>
              <option value="AVAILABLE">on sale with a disclosure</option>
            </select>
          </Field>
          <Button variant="primary" disabled={!can('performances.manage') || initialize.isPending} onClick={() => initialize.mutate()}>
            {initialize.isPending ? 'Initializing…' : 'Initialize inventory'}
          </Button>
          <span className="text-xs text-ink-400">Assign price zones first (Tickets & zones); units are priced as they are created, though zones can be remapped later.</span>
        </div>
      </Panel>
    );
  }

  const data = summary.data;
  const states = Object.entries(data.by_state ?? {}).sort((a, b) => b[1] - a[1]);
  // A unit whose zone never resolved, or whose zone was assigned after the
  // inventory was created. It cannot be sold until it has one.
  const unzonedRows = (data.by_zone ?? []).filter((row) => !row.price_zone_id);
  const unzoned = unzonedRows.reduce((n, row) => n + row.n, 0);
  // What has no zone decides where somebody is sent (D-187): a room that is all
  // general admission has no seats to paint, and the zone map is blank.
  const unzonedGa = unzonedRows.filter((row) => row.unit_kind === 'GA').reduce((n, row) => n + row.n, 0);
  const unzonedSeats = unzoned - unzonedGa;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <div className="rounded-lg border border-line bg-surface px-4 py-3">
          <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Units</p>
          <p className="tabular text-2xl font-bold text-ink-900">{data.total}</p>
          <p className="text-xs text-ink-400">{data.cart_held} in carts right now · v{data.inventory_version}</p>
        </div>
        {states.map(([state, n]) => (
          <div key={state} className="rounded-lg border border-line bg-surface px-4 py-3">
            <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-ink-400 uppercase">
              <span className="inline-block h-3 w-3 rounded-full border border-line" style={{ background: STATE_FILL[state] ?? '#d9d9d9' }} />
              {humanize(state)}
            </p>
            <p className="tabular text-2xl font-bold text-ink-900">{n}</p>
          </div>
        ))}
        <div className="rounded-lg border border-line bg-surface px-4 py-3">
          <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-ink-400 uppercase">
            <span className="inline-block h-3 w-3 rounded-full border border-line" style={{ background: CART_FILL }} /> in a cart
          </p>
          <p className="tabular text-2xl font-bold text-ink-900">{data.cart_held}</p>
        </div>
      </div>

      {unzoned > 0 ? (
        <div className="rounded-lg border border-bad/40 bg-bad/5 px-4 py-3 text-sm text-ink-900">
          <p className="font-semibold">
            {unzoned} unit{unzoned === 1 ? ' has' : 's have'} no price zone.
          </p>
          <p className="mt-1 text-ink-400">
            A unit without a zone has no price: the seat map still shows one, because it reads the assignments directly, but the cart shows nothing and checkout refuses the
            order.{' '}
            {unzonedSeats === 0
              ? 'These are general admission, so there is nothing to paint on the map — give each area a zone from the list under Tickets & zones › Paint zones on the map.'
              : unzonedGa === 0
                ? 'Assign the seats a zone under Tickets & zones.'
                : `${unzonedSeats} seat${unzonedSeats === 1 ? '' : 's'} and ${unzonedGa} general-admission place${unzonedGa === 1 ? '' : 's'}: paint the seats, and give each GA area a zone from the list below the map.`}{' '}
            Saving that remaps this {dateWords().one} by itself — or press Remap price zones below.
          </p>
        </div>
      ) : null}

      <Panel
        title="Seat map"
        description="Colored by inventory state. Click a seat to see its history; shift-click or shift-drag to select many, then apply a change below."
        actions={can('pricing.manage') ? <Button onClick={() => remap.mutate()} disabled={remap.isPending}>Remap price zones</Button> : null}
      >
        <div className="px-5 py-4">
          {geometry ? (
            <SeatMapView layout={geometry} mode="edit" backgroundUrl={layout.data?.manifest.background_url ?? null} seatFills={seatFills} selectedSeatIds={selection.selected} onSeatClick={onSeatClick} onMarqueeSelect={selection.marquee} className="overflow-hidden rounded border border-line" />
          ) : (
            <p className="text-sm text-ink-400">Loading layout…</p>
          )}
          {inspect ? (
            <div className="mt-3 rounded border border-line bg-page/60 p-3 text-sm">
              <p className="font-semibold text-ink-900">
                {inspect.section_code} · row {inspect.row_label} · seat {inspect.seat_number}{' '}
                <Badge tone={inspect.state === 'AVAILABLE' ? 'ok' : inspect.state === 'SOLD' ? 'accent' : 'neutral'}>{inspect.state.replaceAll('_', ' ')}</Badge>
                {inspect.cart_held ? <Badge tone="warn">in a cart</Badge> : null}
                <span className="ml-2 font-normal text-ink-400">v{inspect.version}</span>
              </p>
              <ul className="mt-1 text-xs text-ink-600">
                {(history.data?.history ?? []).map((h: any) => (
                  <li key={String(h.id)}>
                    v{String(h.version)}: {h.from_state ? `${humanize(String(h.from_state))} → ` : ''}
                    {humanize(String(h.to_state))} · {humanize(String(h.operation_type))}
                    {h.reason ? ` · ${String(h.reason)}` : ''} · {String(h.occurred_at)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </Panel>

      {canChange ? (
        <Panel title="Change inventory" description={`Holds, kills, restricted pools and disabling go through the ${dateWords().one}'s inventory coordinator and are recorded in the unit history.`}>
          <div className="space-y-3 px-5 py-4">
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Action" htmlFor="inv-action">
                <select id="inv-action" value={action} onChange={(e) => setAction(e.target.value as typeof action)} className={INPUT}>
                  {can('inventory.hold.place') ? <option value="hold">place a hold</option> : null}
                  {can('inventory.kill') ? <option value="kill">production kill</option> : null}
                  {can('inventory.kill') ? <option value="unkill">release a kill</option> : null}
                  {can('inventory.pool.manage') ? <option value="pool">restricted pool</option> : null}
                  {can('inventory.kill') ? <option value="disable">disable</option> : null}
                  {can('inventory.kill') ? <option value="enable">enable</option> : null}
                </select>
              </Field>
              {action === 'hold' ? (
                <>
                  <Field label="Hold class" htmlFor="inv-class">
                    <select id="inv-class" value={holdClass} onChange={(e) => setHoldClass(e.target.value)} className={INPUT}>
                      {HOLD_CLASSES.map((h) => (
                        <option key={h} value={h}>
                          {humanize(h)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Hold name" htmlFor="inv-hold-name">
                    <input id="inv-hold-name" value={holdName} onChange={(e) => setHoldName(e.target.value)} placeholder="Artist guest list" className={`w-56 ${INPUT}`} />
                  </Field>
                  {/*
                    When the seats come back on their own (D-257). The column has
                    existed since holds were built and nothing acted on it; now a
                    sweeper does, so setting it here finally means something.
                    Left empty, the hold waits for a person.
                  */}
                  <Field label="Release automatically" htmlFor="inv-hold-release" hint="Optional. The seats go back on sale at this time without anybody doing anything.">
                    <input
                      id="inv-hold-release"
                      type="datetime-local"
                      value={holdRelease}
                      onChange={(e) => setHoldRelease(e.target.value)}
                      className={`w-56 ${INPUT}`}
                    />
                  </Field>
                </>
              ) : null}
              {action === 'pool' ? (
                <Field label="Pool" htmlFor="inv-pool">
                  <select id="inv-pool" value={pool} onChange={(e) => setPool(e.target.value as typeof pool)} className={INPUT}>
                    <option value="ADA">ADA / wheelchair</option>
                    <option value="COMPANION">companion</option>
                    <option value="RESTRICTED_VIEW">restricted view</option>
                    <option value="RELEASE">release to available</option>
                  </select>
                </Field>
              ) : null}
              <Field label="Apply to" htmlFor="inv-scope">
                <select id="inv-scope" value={scope} onChange={(e) => { setScope(e.target.value as typeof scope); setScopeId(''); }} className={INPUT}>
                  <option value="selected">selected seats ({selectedUnitIds.length})</option>
                  {(layout.data?.tables ?? []).length > 0 ? <option value="tables">whole tables ({tableIds.length})</option> : null}
                  <option value="section">a whole section</option>
                  <option value="row">a whole row</option>
                  <option value="zone">a price zone</option>
                </select>
              </Field>
              {scope === 'tables' ? (
                <fieldset className="flex flex-wrap gap-1.5">
                  <legend className="sr-only">Tables</legend>
                  {[...(layout.data?.tables ?? [])]
                    .sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { numeric: true }))
                    .map((table) => {
                      const on = tableIds.includes(table.id);
                      return (
                        <label
                          key={table.id}
                          className={`cursor-pointer border px-3 py-1.5 text-sm ${on ? 'border-brand bg-brand text-white' : 'border-line bg-surface text-ink-700 hover:border-brand'}`}
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={on}
                            onChange={() => setTableIds(on ? tableIds.filter((id) => id !== table.id) : [...tableIds, table.id])}
                          />
                          {table.label}
                        </label>
                      );
                    })}
                </fieldset>
              ) : null}
              {scope === 'section' ? (
                <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} className={INPUT} aria-label="Section">
                  <option value="">— section —</option>
                  {(layout.data?.sections ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              ) : null}
              {scope === 'row' ? (
                <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} className={INPUT} aria-label="Row">
                  <option value="">— row —</option>
                  {(layout.data?.rows ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {layout.data?.sections.find((s) => s.id === r.section_id)?.code} · row {r.label}
                    </option>
                  ))}
                </select>
              ) : null}
              {scope === 'zone' ? (
                <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} className={INPUT} aria-label="Zone">
                  <option value="">— zone —</option>
                  {detail.price_zones.map((z) => (
                    <option key={z.id} value={z.id}>
                      {z.name}
                    </option>
                  ))}
                </select>
              ) : null}
              <Field label="Reason" htmlFor="inv-reason">
                <input id="inv-reason" value={reason} onChange={(e) => setReason(e.target.value)} className={`w-56 ${INPUT}`} />
              </Field>
              {can('inventory.preempt_cart') ? (
                <label className="flex items-center gap-1 pb-1.5 text-sm">
                  <input type="checkbox" checked={preempt} onChange={(e) => setPreempt(e.target.checked)} /> take from carts
                </label>
              ) : null}
              <Button variant="primary" disabled={!targetReady || apply.isPending} onClick={() => apply.mutate()}>
                {apply.isPending ? 'Applying…' : 'Apply'}
              </Button>
              {selection.selected.size > 0 ? <Button onClick={selection.clear}>Clear selection</Button> : null}
            </div>
          </div>
        </Panel>
      ) : null}

      <Panel title="Holds" description="Named groups of withheld seats. Release returns them to sale; convert changes the class.">
        <Table headers={['Hold', 'Class', 'Units', 'Owner', 'Created', '']}>
          {(data.holds ?? []).length === 0 ? (
            <EmptyRow columns={6}>No holds on this {dateWords().one}.</EmptyRow>
          ) : (
            data.holds!.map((h) => (
              <tr key={h.id} className={`border-b border-line last:border-0 ${h.closed_at ? 'opacity-50' : ''}`}>
                <td className="px-5 py-3">
                  <div className="font-medium text-ink-900">{h.name}</div>
                  {h.notes ? <div className="text-xs text-ink-400">{h.notes}</div> : null}
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">
                  <span className="mr-1 inline-block h-3 w-3 rounded-full align-middle" style={{ background: STATE_FILL[h.inventory_state] }} />
                  {humanize(h.hold_class)}
                </td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">{h.units}</td>
                <td className="px-5 py-3 text-sm text-ink-600">{h.owner_name ?? '—'}</td>
                <td className="px-5 py-3 text-xs text-ink-400">{h.created_at}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {!h.closed_at && h.units > 0 ? (
                    <>
                      {can('inventory.hold.convert') ? (
                        <select
                          aria-label="Convert hold class"
                          value=""
                          onChange={(e) => {
                            if (e.target.value) convertHold.mutate({ holdId: h.id, code: e.target.value });
                          }}
                          className={INPUT}
                        >
                          <option value="">convert to…</option>
                          {HOLD_CLASSES.filter((c) => c !== h.hold_class).map((c) => (
                            <option key={c} value={c}>
                              {humanize(c)}
                            </option>
                          ))}
                        </select>
                      ) : null}
                      {can('inventory.hold.release') ? (
                        <Button className="ml-2" onClick={() => releaseHold.mutate(h.id)} disabled={releaseHold.isPending}>
                          Release all
                        </Button>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-xs text-ink-400">closed</span>
                  )}
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}
